import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  IcBarChart,
  IcBanknote,
  IcReceipt,
  IcCoins,
  IcReceiptText,
  IcWallet,
  IcTrophy,
  IcLayers,
  IcWarehouse,
  IcShoppingCart,
  IcShoppingBag,
  IcTruck,
  IcSparkles,
} from '../../theme/icons';
import PurchaseSalesBarChart from '../../components/PurchaseSalesBarChart';
import statisticsService from '../../services/statisticsService';
import { fmt, PeriodFilterButton, WhiteCard, CategoryBars } from '../components/statsCommon';
import { StatCard, Delta } from '../../pages/statistics/statsUi';

// Journal d'actions — Étiquettes Ki Para §10 : une variante par type,
// toujours avec icône (jamais la couleur seule).
const ACTION_META = {
  vente: { label: 'Vente', cls: 'bdg-pos', Icon: IcShoppingCart },
  achat: { label: 'Achat', cls: 'bdg-achat', Icon: IcShoppingBag },
  charge: { label: 'Charge', cls: 'bdg-charge', Icon: IcBanknote },
  'paiement fournisseur': { label: 'Paiement fournisseur', cls: 'bdg-payfour', Icon: IcTruck },
  soin: { label: 'Soin', cls: 'bdg-type-soin', Icon: IcSparkles },
};
const fmtActionDate = (d) => { try { return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch (_) { return d; } };

export default function MobileGeneralStatistics() {
  const [period, setPeriod] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    statisticsService.getStatistics(period)
      .then((d) => { if (alive) { setData(d); setError(null); } })
      .catch(() => { if (alive) setError('Impossible de charger les statistiques.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [period]);

  const s = data?.summary || {};
  const pr = data?.prev || {};
  const achats = data?.achats || {};
  const outstanding = data?.outstanding || {};
  const stock = data?.stock || {};
  const netPositive = (s.benefice_net || 0) >= 0;

  return (
    <div className="w-full px-4 py-6">
      {/* Nom de page = chip blanc bordure noire, comme MobileProductList
          (Studio 2026-08-14) ; ancien h1 blanc retiré. */}
      {/* Filtre de période sur sa PROPRE ligne sous le titre, aligné à
          droite (Ahmed 2026-08-17) ; le libellé « 30 derniers jours »
          est retiré (Studio 2026-08-17). */}
      {/* Nom de page = pill blanche, texte encre 700 (Brand Kit §06 mock). */}
      <div className="mb-3">
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold bg-white text-gray-900">
          <IcBarChart size={13} /> Statistique générale
        </span>
      </div>
      <PeriodFilterButton period={period} setPeriod={setPeriod} align="right" />

      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-4 mb-4 rounded-xl">{error}</div>}

      {loading && !data ? (
        <div className="text-center py-20"><div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>
      ) : (
        <div className="space-y-5">
          {/* Achats vs Ventes — suit le filtre de période ; la granularité
              (jour / semaine / mois) vient du backend. */}
          {/* Barres sans fond blanc (Ahmed 2026-08-16). Titre posé sur le fond
              sombre : `mk-dark` = remap clair (mobile-theme.css). */}
          <div className="mk-dark">
            <div className="flex items-center justify-between gap-2 mb-2 px-1">
              {/* Titre de graphe sur fond sombre = #F0FDF4 (green-50), livre §06. */}
              <h2 className="font-semibold text-green-50 flex items-center gap-2 min-w-0">
                <IcBarChart className="text-green-50 shrink-0" /> <span className="truncate">Achats vs Ventes</span>
              </h2>
              <span className="text-xs text-gray-400 shrink-0">
                {{ day: 'par jour', week: 'par semaine', month: 'par mois' }[data?.compare_step] || ''}
              </span>
            </div>
            <PurchaseSalesBarChart data={data?.weekly_compare} granularity={data?.compare_step} transparent />
          </div>

          {/* KPI — mêmes cartes que la page PC (StatCard desktop) : même
              ordre, même détail, mêmes couleurs (Ahmed 2026-08-17). */}
          {/* Chaque carte KPI seule sur sa ligne (Ahmed 2026-08-17). */}
          <div className="grid grid-cols-1 gap-3">
            {/* CA + coût des ventes dans la même carte (comme PC) — taille
                normale (demi-largeur), retour demandé Ahmed 2026-08-17. */}
            <StatCard icon={IcBanknote} label="Chiffre d'affaires" tone="green"
              value={fmt(s.ca)} curr={s.ca} prev={pr.ca}>
              <div className="mt-1 flex items-baseline gap-x-2 text-[11px] min-w-0">
                <span className="text-gray-500 truncate">Capital marchandise vendu</span>
                <span className="shrink-0 text-sm font-bold tabular-nums text-red-700">{fmt(s.capital)}</span>
                <span className="shrink-0"><Delta curr={s.capital} prev={pr.capital} invert /></span>
              </div>
            </StatCard>
            <StatCard icon={IcCoins} label="Bénéfice net" tone={netPositive ? 'green' : 'red'}
              value={fmt(s.benefice_net)} curr={s.benefice_net} prev={pr.benefice_net}>
              <div className="mt-1 flex items-baseline justify-between gap-x-2 text-[11px] min-w-0">
                <span className="text-gray-500 truncate">CA − coût − charges</span>
                <Link to="/statistiques/ventes"
                  className={`shrink-0 font-semibold transition-colors ${
                    netPositive ? 'text-green-700 hover:text-green-900' : 'text-red-700 hover:text-red-900'
                  }`}>
                  Détail des ventes →
                </Link>
              </div>
            </StatCard>
            <StatCard icon={IcWallet} label="Crédits clients" tone="red"
              value={fmt(outstanding.total)}
              sub={`${outstanding.count || 0} vente(s) à crédit — total`} />
            <StatCard icon={IcReceiptText} label="Dette fournisseurs" tone="amber"
              value={fmt(achats.outstanding)}>
              <div className="text-[11px] text-gray-500 mt-1"
                title={`${achats.outstanding_count || 0} facture(s) impayée(s) · ${achats.count || 0} facture(s) sur la période`}>
                Achats {fmt(achats.total)} · payé {fmt(achats.paid)}
              </div>
            </StatCard>

            {/* Charges — le détail des charges payées respire. */}
            <StatCard icon={IcReceiptText} label="Charges" tone="amber"
              value={fmt(s.charges)} curr={s.charges} prev={pr.charges} invert>
              {(data?.charges_detail || []).length > 0 ? (
                <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px] text-gray-500">
                  {data.charges_detail.map((c, i) => (
                    <span key={i} className="whitespace-nowrap">
                      {i > 0 && <span className="mr-2 text-gray-300">·</span>}
                      {c.name} : <span className="tabular-nums">{fmt(c.total)}</span>
                    </span>
                  ))}
                </div>
              ) : (
                <div className="text-[11px] text-gray-400 mt-1">Aucune charge payée sur la période</div>
              )}
            </StatCard>

            {/* Case Stock. */}
            <div
              className="bg-white rounded-2xl border border-gray-200 p-4 min-w-0"
              title={`${stock.valeur_achat > 0
                ? `Marge potentielle +${(((stock.valeur_vente || 0) / stock.valeur_achat - 1) * 100).toFixed(0)}% · `
                : ''}${stock.stock_bas || 0} produit(s) sous le stock minimum`}
            >
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="h-7 w-7 shrink-0 rounded-lg flex items-center justify-center bg-amber-50 text-amber-600">
                  <IcWarehouse size={12} />
                </span>
                <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Stock</span>
              </div>
              <div className="grid grid-cols-2 divide-x divide-gray-100 text-center">
                <div className="pr-3 min-w-0">
                  <div className="text-[11px] text-gray-500 mb-0.5">Valeur</div>
                  <div className="text-lg font-bold tabular-nums truncate text-amber-700">{fmt(stock.valeur_achat)}</div>
                </div>
                <div className="pl-3 min-w-0">
                  <div className="text-[11px] text-gray-500 mb-0.5">Vente</div>
                  <div className="text-lg font-bold tabular-nums truncate text-blue-700">{fmt(stock.valeur_vente)}</div>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-gray-100 text-[11px] text-gray-500 text-center">
                {stock.qty_total || 0} article(s) · {stock.en_stock || 0} produit(s) en stock ·{' '}
                <Link to="/products" className="font-semibold text-red-600 hover:text-red-800">
                  {stock.rupture || 0} rupture(s)
                </Link>
              </div>
            </div>
          </div>

          {/* Top products (achat) */}
          <WhiteCard title="Top produits (achat)" icon={IcTrophy} iconClass="text-amber-500">
            {(!data?.top_achat_products || data.top_achat_products.length === 0) ? (
              <p className="text-gray-400 text-sm text-center py-8">Aucun achat sur la période.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-400 border-b border-gray-100">
                      <th className="py-2 font-medium">Produit</th>
                      <th className="py-2 font-medium text-right">Qté</th>
                      <th className="py-2 font-medium text-right">Achat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_achat_products.map((t, i) => (
                      <tr key={i} className="border-b border-gray-50">
                        <td className="py-2 pr-2 font-medium text-gray-800 truncate max-w-[180px]">{t.name}</td>
                        <td className="py-2 text-right text-gray-600">{t.qty}</td>
                        <td className="py-2 text-right font-semibold text-blue-700">{fmt(t.montant)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </WhiteCard>

          {/* Top categories (achat) */}
          <WhiteCard title="Top catégories (achat)" icon={IcLayers} iconClass="text-blue-600">
            <CategoryBars items={data?.top_achat_categories} valueKey="montant" />
          </WhiteCard>

          {/* Historique d'action */}
          <WhiteCard title="Historique d'action" icon={IcReceipt} iconClass="text-gray-500">
            {(!data?.actions || data.actions.length === 0) ? (
              <p className="text-gray-400 text-sm text-center py-8">Aucune action sur la période.</p>
            ) : (
              <div className="divide-y divide-gray-50 max-h-[28rem] overflow-y-auto">
                {data.actions.map((a, i) => {
                  const meta = ACTION_META[a.type] || { label: a.type, cls: 'bdg-neutral', Icon: IcReceipt };
                  return (
                    <div key={i} className="flex items-center gap-2 py-2.5">
                      <span className={`bdg shrink-0 max-w-[38%] ${meta.cls} px-2 py-0.5 rounded-full text-[11px]`}><meta.Icon size={11} className="shrink-0" /> <span className="truncate">{meta.label}</span></span>
                      <span className="min-w-0 flex-1 text-sm text-gray-700 truncate">{a.label}</span>
                      <span className="min-w-0 truncate text-[11px] text-gray-400">{fmtActionDate(a.date)}</span>
                      <span className="shrink-0 text-sm font-semibold text-gray-800 text-right tabular-nums">{fmt(a.amount)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </WhiteCard>
        </div>
      )}
    </div>
  );
}
